/**
 * Adds an item to the list
 * @param list to be added to
 * @param item to be added
 */
export const addToList = <T>(list: T[], item: T): T[] => [...list, item];

/**
 * Returns the list with the item added at the specified location
 * @param list to be added to
 * @param item to be added
 * @param index in the list where `item` will be added
 */
export function addToListAtIndex<T>(list: T[], item: T, index: number): T[] {
  if (index >= 0 && index < list.length) {
    const tempArray = list;

    tempArray.splice(index, 0, item);

    return tempArray;
  }

  return [];
}

/**
 * Adds the given item to the front of the list
 * @param list to be added to
 * @param item to add
 */
export function addToStartOfList<T>(list: T[], item: T): T[] {
  return addToListAtIndex(list, item, 0);
}

/**
 * Removes the element at the specified index from the list
 * @param list to remove the element from
 * @param index of the element to remove
 */
export function removeFromListAtIndex<T>(list: T[], index: number): T[] {
  if (index >= 0 && index < list.length) {
    const tempArray = list;

    tempArray.splice(index, 1);

    return tempArray;
  }

  return [];
}

/**
 * Removes items from the list for which the predicate returns true
 * @param list to be updated
 * @param predicate to run on all elements of the list
 * @param item optional item to compare elements to, passed to the predicate as a second argument
 */
export function removeFromListByPredicate<T>(
  list: T[],
  predicate: (element: T, item: T) => Record<string, unknown>,
  item: T,
) {
  return list.filter((element) => !predicate(element, item));
}

/**
 * Adds all items to the end of the list
 * @param list to be added to
 * @param items to add to the list
 */
export function addAllToList<T>(list: T[], items: T[]): T[] {
  return [...list, ...items];
}

/**
 * Returns a new list with items that satisfy the predicate overwriting properties of the element they matched
 * @param list to be updated
 * @param predicate that takes an element in the list and the item, and returns true if they match
 * @param item to update in the list
 *
 * Note that this works best with typed Arrays. Array<any> with multiple types for elements could have
 * unpredictable results, so be cautious using for such arrays.
 */
export function updateObjectInList<T extends object>(
  list: T[],
  predicate: (element: T, item: T) => Record<string, unknown> | number,
  item: T,
): T[] {
  if (item instanceof Object) {
    return list.map((element, index) =>
      typeof predicate === 'function'
        ? predicate(element, item)
          ? Object.assign({}, element, item)
          : element
        : index === predicate
          ? Object.assign({}, element, item)
          : element,
    );
  } else {
    return list.map((element, index) =>
      typeof predicate === 'function'
        ? predicate(element, item)
          ? item
          : element
        : index === predicate
          ? item
          : element,
    );
  }
}

/**
 * Returns the first item in the list for which the checked property matches the given value
 * @param list to search
 * @param propToCheck for each element
 * @param propValue to compare with at the given prop
 *
 * Should be updated to use the existing equality checker
 */
export function findItemByProperty<T extends object>(
  list: T[],
  propToCheck: string,
  propValue: T[keyof T],
): T | undefined {
  return list.find((element: any) => element[propToCheck] === propValue);
}

/**
 * Returns all elements for which the checked prop matches the given value
 * @param list to search
 * @param propToCheck for each element
 * @param propValue to compare with at the given prop
 *
 * Should be updated to use the existing equality checker
 */
export function findAllItemsByProperty<T extends object>(
  list: T[],
  propToCheck: string,
  propValue: T[keyof T],
): T[] {
  return list.filter((element: any) => element[propToCheck] === propValue);
}

/**
 * Returns all elements on which the checked prop exists
 * @param list to search
 * @param property for each element
 */
export function findAllItemsWithProperty<T extends object>(
  list: T[],
  property: string,
): T[] {
  return list.filter((element: any) => !!element[property]);
}

/**
 * Returns the index for the first element for which the checked prop matches the given value
 * @param list to search
 * @param propToCheck for each element
 * @param propValue to compare with at the given prop
 */
export function getIndexOfItem<T>(
  list: T[],
  propToCheck: string,
  propValue: T[keyof T],
): number {
  return list.findIndex((element: any) => element[propToCheck] === propValue);
}

export interface GroupByAccumulator<T> {
  [key: string]: T[];
}

export function groupBy<T>(list: T[], prop: string): GroupByAccumulator<T> {
  return list.reduce(
    (accumulator: GroupByAccumulator<T>, element: any) => {
      const value = element[prop];

      if (Object.prototype.hasOwnProperty.call(accumulator, value)) {
        accumulator[value].push(element);
      } else {
        accumulator[value] = [element];
      }

      return accumulator;
    },
    <GroupByAccumulator<T>>{},
  );
}

/**
 * Returns the unique element based on the provided prop
 * @param list to filter
 * @param prop based on remove will perform
 */
export const dedupeArrayByProp = <T>(list: T[], prop: T[keyof T]): T[] =>
  list.filter(
    (value: any, index: number, self: T[]) =>
      self.findIndex((element: any) => element[prop] === value[prop]) === index,
  );

export const removeEmptyValuesAndObjects = (
  array: (string | object)[],
): string[] =>
  array.filter(
    (item: string | object) =>
      item !== null &&
      item !== undefined &&
      item !== '' &&
      (typeof item !== 'object' || Object.keys(item).length !== 0),
  ) as string[];
